/**
 * Persisted AR-launch marker — the only telemetry that can catch a launch
 * failure that JS itself cannot self-report.
 *
 * The Pixel-9 "Starting AR…" hang routes around every wired reportError: a
 * pending `enterXRAsync` throws nothing, a main-thread block stops the 60s
 * watchdog timer, and a GPU-process kill destroys the JS context outright. In
 * all three the page never reports anything.
 *
 * The pattern here is the standard one for un-self-reportable crashes: stamp a
 * marker into localStorage when a launch BEGINS, advance it through each boot
 * stage, and CLEAR it the moment the launch reaches a terminal state (entered
 * AR, or surfaced an error the user can see). If a marker is still present on
 * the NEXT page load, the previous launch died silently — we report it then,
 * with the last stage it reached + the device fingerprint, so a stuck-at
 * `xr-start-called` (never `in-xr`) on a Pixel 9 vs. a clean Pixel 7 directly
 * isolates the wedge.
 *
 * Every function is storage-guarded and never throws.
 */

import { reportError } from "./report";
import { collectDeviceContext, type DeviceContext } from "./deviceContext";

const KEY = "mmr_ar_launch";

/** Ordered boot milestones — the stage stored is the furthest point reached. */
export type LaunchStage =
  | "camera-requested"
  | "camera-granted"
  | "app-clip-begin"
  | "ensure-started"
  | "venue-primed"
  | "perms-ready"
  | "flock-loaded"
  | "xr-start-called"
  | "in-xr";

interface LaunchRecord {
  stage: LaunchStage;
  startedAt: number;
  updatedAt: number;
  entryPoint?: string;
  device?: DeviceContext;
}

function read(): LaunchRecord | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LaunchRecord;
  } catch {
    return null;
  }
}

function write(rec: LaunchRecord): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(rec));
  } catch {
    /* private mode / quota — best effort */
  }
}

function clear(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* never throw */
  }
}

/** Begin a launch attempt. Stamps the marker with the device fingerprint. */
export function beginLaunch(stage: LaunchStage = "camera-requested", entryPoint?: string): void {
  try {
    const now = Date.now();
    write({ stage, startedAt: now, updatedAt: now, entryPoint, device: collectDeviceContext() });
  } catch {
    /* never throw */
  }
}

/** Advance the marker to a later boot stage. No-op if no launch is in flight. */
export function updateLaunchStage(stage: LaunchStage): void {
  try {
    const rec = read();
    if (!rec) return;
    rec.stage = stage;
    rec.updatedAt = Date.now();
    write(rec);
  } catch {
    /* never throw */
  }
}

/** The stage the in-flight launch has reached, or null. Used by the watchdog. */
export function getLaunchStage(): LaunchStage | null {
  return read()?.stage ?? null;
}

/** Terminal: the launch reached a known end state (entered AR, or a surfaced
 * error). Removes the marker so it isn't mistaken for a silent failure on the
 * next load. */
export function completeLaunch(): void {
  clear();
}

/**
 * Call ONCE on app mount, before any beginLaunch. If a marker survived from a
 * prior page load, that launch never completed and never self-reported —
 * report it now with its last stage + device fingerprint, then clear.
 */
export function reportStaleLaunch(): void {
  try {
    const rec = read();
    if (!rec) return;
    clear();
    // Don't report a launch that reached IN_XR — completeLaunch should have
    // cleared it, but if the tab was killed right after entering AR we don't
    // want a false "did not complete".
    if (rec.stage === "in-xr") return;
    reportError(new Error(`AR launch did not complete (prior session, stuck at "${rec.stage}")`), {
      where: "stale-launch",
      stage: rec.stage,
      entryPoint: rec.entryPoint,
      ageMs: Date.now() - rec.startedAt,
      ...rec.device,
    });
  } catch {
    /* never throw */
  }
}
